import React, { useState, useEffect } from 'react';

// Available colors from non-customizable tabs
const availableColors = [
    {
      name: 'Blue (Quick copy)',
      bgcolor: 'bg-blue-300',
      nocolor: 'bg-blue-100',
      hovercolor: 'hover:bg-blue-200',
      hex: '#93C5FD',
      rgb: 'rgb(147, 197, 253)',
      tailwind: 'bg-blue-300'
    },
    {
      name: 'Purple (Folder)',
      bgcolor: 'bg-purple-300',
      nocolor: 'bg-purple-100',
      hovercolor: 'hover:bg-purple-200',
      hex: '#D8B4FE',
      rgb: 'rgb(216, 180, 254)',
      tailwind: 'bg-purple-300'
    },
    {
      name: 'Red (Edgemarc)',
      bgcolor: 'bg-red-300',
      nocolor: 'bg-red-100',
      hovercolor: 'hover:bg-red-200',
      hex: '#FCA5A5',
      rgb: 'rgb(252, 165, 165)',
      tailwind: 'bg-red-300'
    },
    {
      name: 'Pink (eFax)',
      bgcolor: 'bg-pink-300',
      nocolor: 'bg-pink-100',
      hovercolor: 'hover:bg-pink-200',
      hex: '#F9A8D4',
      rgb: 'rgb(249, 168, 212)',
      tailwind: 'bg-pink-300'
    },
    {
      name: 'Yellow (Whois)',
      bgcolor: 'bg-yellow-300',
      nocolor: 'bg-yellow-100',
      hovercolor: 'hover:bg-yellow-200',
      hex: '#FDE047',
      rgb: 'rgb(253, 224, 71)',
      tailwind: 'bg-yellow-300'
    },
    {
      name: 'Green (Dispatch)',
      bgcolor: 'bg-[#CCDFA9]',
      nocolor: 'bg-green-100',
      hovercolor: 'hover:bg-green-200',
      hex: '#CCDFA9',
      rgb: 'rgb(204, 223, 169)',
      tailwind: 'bg-[#CCDFA9]'
    },
    {
      name: 'Orange (Field Engineer)',
      bgcolor: 'bg-[#FFD4A3]',
      nocolor: 'bg-orange-100',
      hovercolor: 'hover:bg-orange-200',
      hex: '#FFD4A3',
      rgb: 'rgb(255, 212, 163)',
      tailwind: 'bg-[#FFD4A3]'
    }
];

const TabEditModal = ({ isOpen, onClose, tabName, tabColor, onSave }) => {
  const [name, setName] = useState(tabName);
  const [selectedColor, setSelectedColor] = useState(tabColor);

  useEffect(() => {
    setName(tabName);

    // Find matching color in availableColors or use first available color as default
    if (tabColor && tabColor.bgcolor) {
      const matchedColor = availableColors.find(
        color => color.bgcolor === tabColor.bgcolor
      );
      setSelectedColor(matchedColor || availableColors[0]);
    } else {
      // Default to first color if no color provided
      setSelectedColor(availableColors[0]);
    }
  }, [tabName, tabColor]);

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim(), selectedColor);
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Edit Tab</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tab Name Input */}
        <div className="mb-6">
          <label htmlFor="tab-name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Tab Name
          </label>
          <input
            id="tab-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
            maxLength={50}
            placeholder="Enter tab name"
          />
        </div>

        {/* Color Selector */}
        <div className="mb-6">
          <div className="block text-sm font-medium text-gray-700 mb-3">
            Tab Color
          </div>
          <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
            {availableColors.map((color) => (
              <div
                key={color.tailwind}
                onClick={() => setSelectedColor(color)}
                className={`flex items-center gap-3 p-3 rounded-md cursor-pointer transition-all border-2 ${
                  selectedColor?.tailwind === color.tailwind
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {/* Color Swatch */}
                <div className="flex-shrink-0">
                  <div
                    className={`w-12 h-12 rounded-md border border-gray-300 ${color.bgcolor}`}
                  ></div>
                </div>

                {/* Color Info */}
                <div className="flex-grow">
                  <div className="font-medium text-gray-800 text-sm">
                    {color.name}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    <div>{color.hex}</div>
                    <div className="text-gray-500">{color.tailwind}</div>
                  </div>
                </div>

                {/* Selected Indicator */}
                {selectedColor?.tailwind === color.tailwind && (
                  <div className="flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                      className="w-6 h-6 text-orange-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default TabEditModal;
